/**
 * useExplorerContextHint
 *
 * Returns a display string describing the explorer's current connection/database selection.
 * Used by command palette items to show what context will be used.
 */

import { useMemo } from 'react';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

export function useExplorerContextHint(): {
  connectionId: string | null;
  connectionName: string | null;
  database: string | null;
  hint: string | null;
} {
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const selectedConnectionId = useDatabaseUIStore((s) => s.selectedConnectionId);
  const selectedDatabase = useDatabaseUIStore((s) => s.selectedDatabase);

  return useMemo(() => {
    const connected = activeConnections.filter((c) => c.status === 'connected');
    if (connected.length === 0)
      return { connectionId: null, connectionName: null, database: null, hint: null };

    // Find the selected connection
    const conn = selectedConnectionId
      ? connected.find((c) => c.id === selectedConnectionId)
      : connected.length === 1
        ? connected[0]
        : null;

    if (!conn) return { connectionId: null, connectionName: null, database: null, hint: null };

    const name = safeConnectionName(conn.connection);
    const db = selectedDatabase || null;

    const hint = db ? `${name} > ${db}` : name;

    return { connectionId: conn.id, connectionName: name, database: db, hint };
  }, [activeConnections, selectedConnectionId, selectedDatabase]);
}
